import { Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { Router } from '@angular/router';
import CookieService from './cookie.service';
import { AppSettingService } from '../app.service';

export const Roles: string[] = [
	"SystemAdmin",
	"ZoneAdmin",
	"SchoolAdmin",
	"Teacher",
	"Student",
	"Employer",
	"Parent",
	"ZoneStaff",
	"SchoolStaff",
	"Other",
	"Anonymous",
]
export type TRole = typeof Roles[number];


export interface IUserOtherAccount {
	PersonId: string,
	Zone: string,
	Role: string
}

export interface IRole {
	RoleId: string,
	Role: TRole,
	RoleName: string
}

export interface IRoleStudent {
	RoleId: string,
	Role: TRole,
	RoleName: string,
	StudentGrade?: string,
	GradeName?: string
}

export interface IUserProfile {
	AllowSignup: boolean,
	AllowedWidgets: string[],
	APIKey: string,
	APIToken: string,
	BirthDate: string,
	Email: string,
	FirstName: string,
	HighestRole: string,
	HomePage: string
	IsPublicZone: boolean,
	LastName: string,
	NeedGlobalAccount: boolean,
	OtherAccounts: IUserOtherAccount[],
	PersonId: string,
	PersonGuid: string,
	PersonRoles: IRoleStudent[],
	ZipCode: string,
	ZoneClient: string,
	ZoneDisplayName: string,
	SessionTimestamp: string
}

@Injectable({
	providedIn: 'root'
})
export class UserInfoService {
	UserInfoTimestamp: any = null;

	constructor(
		private http: HttpClient,
		private router: Router
	) {
		this.UserInfoTimestamp = new Rx.BehaviorSubject(this.getProfile().SessionTimestamp);
		window.setInterval(() => {
			try {
				if (CookieService.getCookie("account")) {
					let acc: IUserProfile = CookieService.getCookie("account");
					if (acc.SessionTimestamp !== this.UserInfoTimestamp.getValue()) {
						console.log("account cookie change.. old:" + this.UserInfoTimestamp.getValue() + "    new:" + this.getProfile().SessionTimestamp);
						this.UserInfoTimestamp.next(this.getProfile().SessionTimestamp);
					}
				}
			}
			catch (ex) {
				console.log(ex);
			}
		}, 1000); // run every 1 second
	}

	private anonymous: IRoleStudent = { RoleId: "255", Role: "Anonymous", RoleName: "Anonymous" };

	private anonymousProfile: IUserProfile = {
		AllowSignup: false,
		AllowedWidgets: ["Signup", "Login"],
		APIKey: "",
		APIToken: "",
		BirthDate: "",
		Email: "",
		FirstName: "",
		HighestRole: "Anonymous",
		HomePage: AppSettingService.baseUrl + "/User/Login",
		IsPublicZone: false,
		LastName: "",
		NeedGlobalAccount: false,
		OtherAccounts: [],
		PersonId: "0",
		PersonGuid: "",
		PersonRoles: [this.anonymous],
		ZipCode: "",
		ZoneClient: "JOURNEYS",
		ZoneDisplayName: "Global Account",
		SessionTimestamp: ""
	}

	getProfile(): IUserProfile {
		let acc: IUserProfile = CookieService.getCookie("account");
		if (acc == null || acc.PersonId == null || acc.PersonGuid == null) {
			this.anonymousProfile.SessionTimestamp = new Date().toUTCString();
			return this.anonymousProfile;
		}
		else {
			return acc;
		}
	}

	clearStorage(): void {
		try { sessionStorage.clear(); } catch (ex) { ; }
		/* do not clean localStorage. It store the idle timeout */
	}

	private signOff(): Rx.Observable<any> {
		return this.http.get<any>(
			AppSettingService.appConfig.JourneyAPIUrl + "Session/SignOff",
			{
				withCredentials: true,
				headers: { 'Authorization': this.authHeader(false) }
			}
		);
	}

	clearProfile(goLogin: boolean): void {
		// info server to logoff
		this.signOff().subscribe({
			next: (response: any) => {
				this.clearStorage();
				if (goLogin) {
					window.location.href = AppSettingService.baseUrl + "/User/Login";
				}
			},
			error: (err: any) => {
				console.log(err);
				this.clearStorage();
				if (goLogin) {
					window.location.href = AppSettingService.baseUrl + "/User/Login";
				}
			}
		});
	}

	isLogin(): boolean {
		try {
			var s = this.getProfile();
			if (s != null && s.PersonId != null && s.PersonId != "" && s.PersonId != "0")
				return true;
			else
				return false;
		}
		catch (ex) {
			return false;
		}
	}

	// force user to login again if no authentication or session expire
	enforceLogin(): void {
		if (!this.isLogin()) {
			this.router.navigateByUrl("/User/Login");
		}
	}

	watchSessionTimestamp(): Rx.Observable<string> {
		return Rx.of(this.getProfile().SessionTimestamp);
	}

	authHeader(openLogin: boolean = false): string {
		try {
			let s = this.getProfile();
			// check if need to openLogin window or not. Only open if the user currently is anonymous
			if (openLogin != null && openLogin == true && this.isLogin() && this.isAnonymous()) {
				// have Circular dependency issue, we cannot openlogin here, have to redirect user to login page
				this.router.navigateByUrl("/User/Login");
				return "Bearer ^^^xxxxxxxxxxxxxxxxxx^^^"; // return falut login info
			}
			if (s.APIToken == undefined) {
				return "";
			}
			else {
				return atob(s.APIToken);
			}
		}
		catch (ex) {
			return "";
		}
	}

	allowSignup(): boolean {
		try {
			var s = this.getProfile();
			return s.AllowSignup === true;
		}
		catch (ex) {
			return false;
		}
	}

	getHomePage(): string {
		return this.getProfile().HomePage;
	}

	getInstitutionCode(): string {
		let institutionCode = '';
		if (this.isAnonymous()) {
			let homepage: string = this.getProfile().HomePage;
			if (homepage.toLowerCase().indexOf("/collegescorecard/school/") > 0) {
				institutionCode = "IPEDS-" + homepage.split("/").pop();
			}
			else if (homepage.toLowerCase().indexOf("/nces/school/") > 0) {
				institutionCode = "NCES-" + homepage.split("/").pop();
			}
		}
		return institutionCode;
	}

	get fullName(): string {
		let profile = this.getProfile();
		return profile.FirstName + " " + profile.LastName;
	}

	get isSystemAdmin(): boolean {
		return this.hasRole(["SystemAdmin"]);
	}

	get isZoneAdmin(): boolean {
		return this.hasRole(["ZoneAdmin"]);
	}

	get isSchoolAdmin(): boolean {
		return this.hasRole(["SchoolAdmin"]);
	}

	get isTeacher(): boolean {
		return this.hasRole(["Teacher"]);
	}

	get isStudent(): boolean {
		return this.hasRole(["Student"]);
	}

	get isEmployer(): boolean {
		return this.hasRole(["Employer"]);
	}

	get isOther(): boolean {
		return this.hasRole(["Other"]);
	}

	isAnonymous(): boolean {
		return this.hasRole(["Anonymous"]);
	}

	isPublicZone(): boolean {
		try {
			return this.getProfile().IsPublicZone;
		}

		catch (ex) {
			return false;
		}
	}

	isJAZone(): boolean {
		return false;
	}

	get anonymousEmail(): string {
		return sessionStorage.getItem('anonymousUser') ?? "";
	}
	set anonymousEmail(v: string) {
		if (v == null) {
			sessionStorage.removeItem("anonymousUser");
		}
		else {
			sessionStorage.setItem('anonymousUser', v);
		}
	}

	get personRoles(): IRoleStudent[] {
		try {
			return this.getProfile().PersonRoles;
		}
		catch {
			return [this.anonymous];
		}
	}

	public hasRole(roles: TRole[]): boolean {
		return this.personRoles.find(x => roles.find(y => y.toLowerCase() === x.Role.toLowerCase())) != undefined;
	}

	get zoneName(): string {
		try {
			return this.getProfile().ZoneClient;
		}
		catch (ex) {
			return '';
		}
	}

	get personId(): string {
		try {
			return this.getProfile().PersonId;
		}
		catch (ex) {
			return '-21232132132';
		}
	}
}
